import { useRef, useState, type DragEvent } from 'react'
import { Alert, Box, Button, Typography } from '@mui/material'
import AddPhotoIcon from '@mui/icons-material/AddPhotoAlternateOutlined'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { refuserFichier, resizeToPortrait } from './image-resize.js'

/**
 * Le portrait d'une personne — un seul visuel, pas une galerie.
 *
 * ⭐ C'est cette image qui l'identifie une fois connectée : entête de
 * l'application, listes d'utilisateurs, fiches. D'où le rendu en cercle ici,
 * fidèle à ce qu'elle verra ensuite — montrer un carré à la saisie puis un
 * cercle à l'usage laisse découvrir le recadrage trop tard.
 *
 * La réduction se fait SUR L'APPAREIL avant l'envoi : une photo de téléphone
 * pèse plusieurs mégaoctets, et elle voyage ensuite dans la réplique de chaque
 * poste.
 */
export function ChoixPortrait({
  value,
  onChange,
  taille = 96,
  compact = false,
  forme = 'ronde',
}: {
  value: string | null
  onChange: (portrait: string | null) => void
  taille?: number
  /**
   * Pastille SEULE, sans les boutons ni la legende.
   *
   * Sert quand la photo doit tenir dans une colonne etroite a cote d'autres
   * champs : les commandes prendraient alors plus de place que la photo
   * elle-meme. Le glisser-deposer et le clic restent actifs sur la pastille,
   * et l'infobulle porte l'explication que la legende donnait.
   */
  compact?: boolean
  /**
   * Ronde pour une PERSONNE, carree pour un LOGO.
   *
   * Un logo est concu dans un cadre rectangulaire : le rogner en cercle ampute
   * ce que son auteur a place dans les coins, souvent le nom de l'enseigne.
   */
  forme?: 'ronde' | 'carree'
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [occupe, setOccupe] = useState(false)
  const [survol, setSurvol] = useState(false)

  async function accepter(fichier: File | undefined): Promise<void> {
    if (!fichier) return
    setErreur(null)
    const refus = refuserFichier(fichier)
    if (refus) {
      setErreur(refus)
      return
    }
    setOccupe(true)
    try {
      onChange(await resizeToPortrait(fichier))
    } catch {
      setErreur('Cette image n’a pas pu être lue.')
    } finally {
      setOccupe(false)
    }
  }

  return (
    /* ⭐ Le glisser-deposer est accepte sur TOUT le bloc, pas seulement sur la
       pastille de 96 px : viser un cercle de la taille d'un timbre avec un
       fichier au bout du curseur est un exercice d'adresse, pas une interface.
       Le retour visuel accompagne la meme surface. */
    <Box
      onDragOver={(e: DragEvent) => {
        e.preventDefault()
        setSurvol(true)
      }}
      onDragLeave={(e: DragEvent) => {
        // `dragleave` se declenche aussi en passant d'un enfant a l'autre :
        // sans ce test, le cadre clignote pendant tout le survol.
        if (!e.currentTarget.contains(e.relatedTarget as Node)) setSurvol(false)
      }}
      onDrop={(e: DragEvent) => {
        e.preventDefault()
        setSurvol(false)
        void accepter(e.dataTransfer.files?.[0])
      }}
      sx={{
        borderRadius: 2,
        p: 2,
        m: -2,
        transition: 'background-color .15s',
        bgcolor: survol ? 'action.hover' : 'transparent',
        outline: survol ? '2px dashed' : 'none',
        outlineColor: 'primary.main',
      }}
    >
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
        <Box
          title={
            compact
              ? 'Glissez une image ou cliquez pour choisir une photo'
              : undefined
          }
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              inputRef.current?.click()
            }
          }}
          role="button"
          tabIndex={0}
          aria-label={value ? 'Changer la photo' : 'Ajouter une photo'}
          sx={{
            position: 'relative',
            width: taille,
            height: taille,
            flexShrink: 0,
            borderRadius: forme === 'ronde' ? '50%' : 2,
            overflow: 'hidden',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            border: value ? 1 : '2px dashed',
            borderColor: survol ? 'primary.main' : value ? 'divider' : 'action.disabled',
            bgcolor: survol ? 'action.hover' : 'background.default',
            opacity: occupe ? 0.6 : 1,
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          {value ? (
            <Box
              component="img"
              src={value}
              alt=""
              sx={{
                // ⚠️ Positionnement ABSOLU, et non `height: 100%` dans la
                // grille : la rangee de la grille se dimensionne sinon sur la
                // hauteur NATURELLE de l'image, si bien que `100%` se resout
                // contre elle — raisonnement circulaire, image qui deborde.
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          ) : (
            <AddPhotoIcon sx={{ fontSize: 28, color: 'text.disabled' }} />
          )}
        </Box>

        {!compact && (
        <Box>
          <Button size="small" onClick={() => inputRef.current?.click()} disabled={occupe}>
            {value ? 'Changer la photo' : 'Ajouter une photo'}
          </Button>
          {value && (
            <Button
              size="small"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => onChange(null)}
            >
              Retirer
            </Button>
          )}
          <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mt: 1 }}>
            Glissez une image ou cliquez. Elle est réduite sur votre appareil.
          </Typography>
        </Box>
        )}
      </Box>

      {erreur && (
        <Alert severity="warning" sx={{ mt: 2, py: 0, px: 1 }}>
          <Typography variant="caption">{erreur}</Typography>
        </Alert>
      )}

      <input
        ref={inputRef}
        hidden
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => {
          void accepter(e.target.files?.[0])
          // Réinitialise, sinon rechoisir LE MÊME fichier ne déclenche rien.
          e.target.value = ''
        }}
      />
    </Box>
  )
}
